<script>
import { FormModel, Input } from 'ant-design-vue'
import { inputComponentNames, selectComponentName, supportFormControlComponent } from './component-register'
import { doNothing } from '@/utils/ops'
import { ACTION_POSITION } from '@/utils/constant'
import BasicBtnGroup from '../basic-btn-group/index.vue'
import { isArray } from '@/utils/is'

const FormModelItem = FormModel.Item

/**
 * @typedef {({
 * name: string, //字段名 , 除wrapperSlot存在以外，其他情形必须非空
 * value?: any, //初始值, 默认null
 * label?: string, //form-item的label
 * ifShow?:(formModel) => boolean, //v-if form-item是否隐藏
 * isShow?:(formModel) => boolean, //v-show form-item是否消失
 * isDisabled?:(formModel) => boolean, //受控组件是否disabled
 * vmodel: (beforeVal) => afterVal, //替代v-model, 转换v-model的值，例如vmodel: (val) => val.trim()
 * } & ({
 * component: string|Component, //受控组件. 为string时，必须在component-register中存在
 * props?: Component.props, //formItem的属性, 不包括value
 * class?: any,//受控组件的className
 * style?: any, //受控组件的样式
 * on?: {}, //事件监听
 * addition?: any, //受控组件的其他属性
 * wrapperProps?: FormModelItem.props, //form-item的props
 * wrapperClass?: string | string[] | object |  null, //form-item的类
 * wrapperAddition?: object //form-item的其他属性
 * } | {
 * slot: string, //自定义受控组件的插槽
 * on: {} //监听
 * })) | {
 * wrapperSlot: string //自定义form-item的插槽
 * }} Schema
 *
 * @typedef {{
 *  schemas: Schema[],
 *  formProps: FormModel.props, //FormModel的属性
 *  wrapperProps: FormModelItem.props //formItem的属性，将被混入schemas.wrapperProps中的
 *  actions?: null | ({loading?: boolean, disabled?: boolean, position?: ACTION_POSITION[keyof ACTION_POSITION]} & ({
 *      props: import('../basic-btn-group/CollaborationAppointment.vue').props
 *  } | {
 *   slot: 'actions' //渲染为scopeSlot的按钮组, slot默认为action, position指示插入的位置
 *  }),
 *  //onSubmit: Function, //异步函数, 提交事件
 *  //onReset: Function //同步或异步函数，重置
 * }} FormMetas
 *
 * @description 没有实现schemas下继续嵌套schemas,因为:
 * 对于CheckboxGroup, RadioGroup 时传入props.options即可实现子元素
 */

// 用途：利用json实现动态的form
// 原理：动态的slot(渲染函数）的应用
/**
 * @type {import('vue').Component}
 */
export default {
  name: 'SchemaForm',
  props: {
    /**
     * @type {FormMetas}
     */
    formMetas: {
      type: Object,
      required: true,
    },
    keepModel: {
      // 动态改变schemas时，是否"保持"formModel, 为true时会保留"历史"存在过的[schema.name], false时重新生成formModel
      type: Boolean,
      default: false,
    },
    /**
     * @type {({reset, submit, validate})}
     */
    forwardRef: {
      type: Function, // 类似于React的forwardRef, 不建议使用, 因为scope-slot已经够用
      default: null,
    },
    size: {
      type: String, // 控件大小 large default small，被混入schema.props
      default: 'default',
    },
  },
  data() {
    const formModel = this.initForm()
    return {
      actionLoading: false,
      actionDisabled: false,
      keep: this.keepModel,
      formModel,
      rules: this.initRules(formModel)
    }
  },
  watch: {
    'formMetas.schemas'(schemas) {
      const oldModel = { ...this.formModel }

      if (this.keepModel) {
        schemas.forEach(({ name, value }) => {
          if (!(name in oldModel) && name) {
            oldModel[name] = value || null
          }
        })
        // 增加了新的字段值
        this.formModel = oldModel
      } else {
        const newModel = {}
        schemas.forEach(({ name, value }) => {
          if (name) {
            newModel[name] = oldModel[name] || value || null
          }
        })
        // 保留了原有值,但是可能去掉了一些字段
        this.formModel = newModel
      }
    },
  },
  methods: {
    async loading(fn) {
      if (this.actionLoading || this.actionDisabled) {
        return
      }
      const btnActions = this.formMetas.actions
      if (btnActions) {
        if (btnActions.loading) {
          this.actionLoading = true
        } else if (btnActions.disabled) {
          this.actionDisabled = true
        }
      }

      try {
        await fn()
      } catch (_) {
      } finally {
        this.actionLoading = false
        this.actionDisabled = false
      }
    },
    initForm() {
      const data = {}
      this.formMetas.schemas.forEach(({ name, value }) => {
        if (name) {
          data[name] = value || null
        }
      })
      return data
    },
    initRules(formModel) {
      const rules = this.formMetas.formProps?.rules

      if (rules) {
        const enhancedRules = {}
        for (const ruleField in rules) {
          enhancedRules[ruleField] = rules[ruleField].map(rule => {
            if (!rule.hocValidator) {
              return rule
            }
            const { hocValidator, ...otherProps } = rule
            otherProps.validator = hocValidator(formModel)
            return otherProps
          })
        }

        return enhancedRules
      }
      return null
    },
    validateFields(fields) {
      return this.$refs.formRef.validate(fields)
    },
    resetForm() {
      const nextModel = this.initForm()
      if (this.formMetas.onReset) {
        const currentModel = { ...this.formModel }
        this.loading(async () => {
          await this.formMetas.onReset(currentModel, nextModel)
        })
      }
      this.$refs.formRef.resetFields()
      this.formModel = nextModel

      this.rules = this.initRules(nextModel)
    },
    sumbitForm() {
      this.loading(async () => {
        const model = { ...this.formModel }
        await this.$refs.formRef.validate()
        if (this.formMetas.onSubmit) {
          await this.formMetas.onSubmit(model, this.$refs.formRef)
        }
      })
    },
    createFormItem(schema, children) {
      const { name, label, isShow } = schema

      let _childs

      if (children) {
        if (isArray(children)) {
          _childs = children
        } else {
          _childs = [children]
        }
      }

      let { wrapperProps = {}, wrapperStyle = {}, wrapperClass = '', wrapperAdditions = {} } = schema

      if (isShow && !isShow(this.formModel)) {
        wrapperStyle = { ...wrapperStyle, display: 'none' }
      }

      return this.$createElement(
        FormModelItem,
        {
          ...wrapperAdditions,
          props: {
            ...(this.formMetas.wrapperProps || {}),
            ...wrapperProps,
            label: label || '',
            prop: name,
          },
          key: name,
          class: wrapperClass,
          style: wrapperStyle
        },
        _childs
      )
    },
    /**
     * @param {Schema[]} schemas
     */
    createFormItems(schemas, actions) {
      const size = this.size
      const formModel = this.formModel
      const scopedSlots = this.$scopedSlots

      /**
       * @type {Vue.VNode[]}
       */
      const vnodes = []

      const h = this.$createElement

      for (const schema of schemas) {
        const { name, ifShow } = schema

        if (!name && schema.wrapperSlot === undefined) {
          throw new Error(
            'schema必须存在非空name属性,或指定wrapperSlot忽略name'
          )
        }

        // ifShow?:(formModel)=>boolean 实现v-if
        if (ifShow && !ifShow(formModel)) {
          continue
        }

        if (schema.wrapperSlot !== undefined) {
          if (scopedSlots[schema.wrapperSlot]) {
            // 渲染自定义form-item
            vnodes.push(
              ...scopedSlots[schema.wrapperSlot]({ model: formModel })
            )
          } else {
            continue
          }
        } else if (schema.slot !== undefined) {
          // 渲染自定义form-control
          if (scopedSlots[schema.slot]) {
            vnodes.push(
              this.createFormItem(
                schema,
                scopedSlots[schema.slot]({ model: formModel })
              )
            )
          } else {
            continue
          }
        } else {
          const component = schema.component || Input

          let formControlComponent = null

          if (
            typeof component === 'string' &&
            supportFormControlComponent[component] !== undefined
          ) {
            formControlComponent = supportFormControlComponent[component]
          } else {
            formControlComponent = component
          }

          if (formControlComponent !== null) {
            // 此处认为受控组件一定支持双向绑定
            const {
              prop: vModelField = 'value',
              event: vModelEvent = 'input',
            } = formControlComponent.model || {}

            const controlEvents = schema.on || {}
            const vModelEventHandler = schema.vmodel || doNothing

            // v-model的event实现
            controlEvents[vModelEvent] = (event) => {
              formModel[name] = vModelEventHandler(event)
            }

            let placeholder = ''
            if (schema.props && schema.props.placeholder) {
              placeholder = schema.props.placeholder
            } else if (inputComponentNames.includes(formControlComponent.name)) {
              placeholder = this.$t('personSetting.PleaseEnter')
            } else if (selectComponentName === formControlComponent.name) {
              placeholder = this.$t('home.PleaseChoose')
            }

            /**
             * @type {Vue.VNodeData}
             */
            const formControlProps = {
              ...(schema.additions || {}),
              props: {
                size,
                ...(schema.props || {}),
                placeholder,
                [vModelField]: formModel[name],
                disabled: schema.isDisabled && schema.isDisabled(formModel)
              },
              on: controlEvents,
              class: schema.class,
              style: schema.style
            }

            vnodes.push(
              this.createFormItem(
                schema,
                h(formControlComponent, formControlProps)
              )
            )
          } else {
            console.warn(`字段${name}使用了不支持的组件${component}`)
            continue
          }
        }
      }

      if (actions !== null) {
        const loading = this.actionLoading
        const disabled = this.actionDisabled

        let actionNode
        if (scopedSlots[actions.slot || 'actions']) {
          actionNode = scopedSlots[actions.slot || 'actions']({
            model: formModel,
            actions: this.forwardRefAction,
            loading,
            disabled,
          })
        } else {
          const { on: actionEvents = {}, ...otherProps } = actions.props || {}
          // 使用默认按钮组
          actionNode = h(BasicBtnGroup, {
            ...otherProps,
            props: {
              loading,
              disabled
            },
            on: {
              defClick: () => {
                return this.resetForm()
              },
              primeClick: () => {
                return this.sumbitForm()
              },
              ...actionEvents,
            },
          })
        }
        switch (actions.position) {
          case ACTION_POSITION.first:
            vnodes.unshift(actionNode)
            break
          case ACTION_POSITION.last:
          default:
            vnodes.push(actionNode)
        }
      }

      return vnodes
    },
  },
  created() {
    this.forwardRefAction = {
      reset: () => this.resetForm(),
      submit: () => this.sumbitForm(),
      validate: (fields) => this.validateFields(fields),
    }
  },
  mounted() {
    this.$nextTick(() => {
      if (this.forwardRef) {
        this.forwardRef(this.forwardRefAction)
      }
    })
  },
  beforeDestroy() {
    this.forwardRefAction = null
  },
  render(h) {
    const { schemas, formProps = {}, actions = null } = this.formMetas

    // 本来想用component is来渲染组件，但当formMetas.schemas.component
    return h(
      FormModel,
      {
        props: {
          ...formProps,
          rules: this.rules,
          model: this.formModel,
        },
        ref: 'formRef',
      },
      this.createFormItems(schemas, actions)
    )
  },
}
</script>
